import type { PlanHuntDrive } from '@wehunt/ui'
import { ENTIRE_GROUND } from '@wehunt/ui'
import type { DraftDriveDetail } from '../useDraftHunt'

/**
 * Wizard drives → draft-detail wire shape. Persists each placed stand's
 * `pinId` + assigned hunter (CLUB-157/158) so picks & assignments survive
 * save/reload/finalize. Unplaced stands (no `pinId`, e.g. rebuilt from a legacy
 * draft's `standCount`) are dropped from `stands` but still counted.
 *
 * `subAreaIds` and `pinIds` are now the drive's source of truth: the multi-select
 * editor writes both directly, so they are sent straight through rather than
 * derived from the retired single `area` field. An absent list (a drive that was
 * never opened in the editor) is the empty list — the whole hunting ground, no
 * pin filtering. The ids are already valid wire members because of how they are
 * minted: a sub-area id is its ground doc id / `areaGroup` (see
 * `resolveDriveBaseAreas`), and a pin id is a real map-pin id from the same
 * space the API and mobile use — never a client-side centroid or composite. The
 * single `area` field is no longer sent on the wire; the .NET DTO keeps it only
 * for legacy read-compat.
 */
export function wizardDrivesToDetail(drives: PlanHuntDrive[]): DraftDriveDetail[] {
  return drives.map((d) => ({
    name: d.name,
    subAreaIds: d.subAreaIds ?? [],
    pinIds: d.pinIds ?? [],
    standCount: d.stands.length,
    stands: d.stands.flatMap((s) =>
      s.pinId !== null ? [{ pinId: s.pinId, assignedUserId: s.assignedId }] : [],
    ),
  }))
}

/**
 * Draft-detail → wizard drives. When the draft has persisted `stands`
 * (CLUB-157), rebuild each with its `pinId` + assignee. Legacy drafts have only
 * `standCount` — rebuild that many unplaced stands (the pre-CLUB-157 behaviour).
 *
 * `subAreaIds`/`pinIds` are read straight back — they are what the multi-select
 * editor reads and writes. A draft saved before `subAreaIds` existed has none at
 * all (`undefined`, not `[]`); for that case alone the legacy single `area` is
 * lifted into a one-element `subAreaIds` so an old drive's base survives into the
 * multi-select. The vestigial `area` field is kept on the wizard drive only for
 * type compatibility (nothing edits it) and mirrors `subAreaIds[0]`.
 */
export function detailDrivesToWizard(details: DraftDriveDetail[]): PlanHuntDrive[] {
  return details.map((d, i) => {
    const legacyArea =
      d.area != null && d.area !== '' && d.area !== ENTIRE_GROUND ? [d.area] : []
    const subAreaIds = d.subAreaIds !== undefined ? d.subAreaIds : legacyArea
    const area = subAreaIds[0] ?? null
    const pinIds = d.pinIds ?? []
    const stands = d.stands ?? []
    if (stands.length > 0) {
      return {
        id: `dr-load-${i}`,
        name: d.name,
        area,
        subAreaIds,
        pinIds,
        stands: stands.map((s, j) => ({
          id: `st-load-${i}-${j}`,
          pinId: s.pinId,
          assignedId: s.assignedUserId,
        })),
      }
    }
    return {
      id: `dr-load-${i}`,
      name: d.name,
      area,
      subAreaIds,
      pinIds,
      stands: Array.from({ length: Math.max(0, d.standCount) }, (_, j) => ({
        id: `st-load-${i}-${j}`,
        pinId: null,
        assignedId: null,
      })),
    }
  })
}
